import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {MenuItem} from '../layout/models/menu.model';
import {AuthService} from '../auth/auth-service';
import {AppMenuItem} from '../../shared/interfaces/menu-config-interface';
import {MenuConfigService} from '../../shared/services/menu-config.service';
import {firstValueFrom} from 'rxjs';
import {StoreDbService} from '../db/store.db.service';
import {environment} from '../../../environments/environment';
import {ProcessAccessService} from './acceso-proceso.service';
import {CorporateAuthorizationService} from './corporate-authorization.service';

@Injectable({ providedIn: 'root' })
export class MenuService {

  private auth = inject(AuthService);
  private menuConfig = inject(MenuConfigService);
  private storeDbService = inject(StoreDbService);
  private readonly processAccessService = inject(ProcessAccessService);
  private readonly corporateAuthorization = inject(CorporateAuthorizationService);
  private readonly rawMenu$ = signal<AppMenuItem[]>([]);
  private resolveReady!: () => void;
  private readonly readyPromise = new Promise<void>(resolve => {
    this.resolveReady = resolve;
  });
  // Este es el menú final ya filtrado
  readonly menu = computed(() => {
    const store = this.storeDbService.store();
    const tipoPV = this.storeDbService.store()?.tipoPV;
    const sociedad = store?.pais;
    return this.filterMenu(this.rawMenu$(), tipoPV, sociedad);
  });
  // Para saber si ya cargo el menu, nos sirve para el guard para pedir el login
  readonly isLoaded = computed(() => this.rawMenu$().length > 0);

  constructor() {
    effect(async () => {
        this.loadInitialData();
    });
  }

  async ready(): Promise<void> {
    return this.readyPromise;
  }

  private async loadInitialData() {

    const menu = await firstValueFrom(this.menuConfig.loadMenu());
    this.rawMenu$.set(menu ?? []);

    // Avisamos que ya está listo
    this.resolveReady();
  }

  private async loadMenu() {
    const menu = await firstValueFrom(this.menuConfig.loadMenu());
    this.rawMenu$.set(menu ?? []);
  }

  async refresh(): Promise<void> {
    await this.loadMenu();
  }

  // Filter Menu
  private filterMenu(
    menu: AppMenuItem[],
    tipoPV: number | undefined,
    sociedad: string | undefined
  ): AppMenuItem[] {

    const currentEnv = environment.envName;

    const recursive = (
      items: AppMenuItem[]
    ): AppMenuItem[] => {

      return items
        .map((item): AppMenuItem | null => {

          const isVisible =
            item.visible !== false;

          const matchesTipo =
            !item.tipoPuntoVenta?.length ||
            (
              tipoPV != null &&
              item.tipoPuntoVenta.includes(tipoPV)
            );

          const matchesEnv =
            !item.ambientes?.length ||
            item.ambientes.includes(currentEnv);

          const matchesSociedad =
            !item.sociedades?.length ||
            (
              sociedad != null &&
              item.sociedades.includes(sociedad)
            );

          const matchesProcess =
            !item.proceso ||
            this.processAccessService.puedeAcceder(
              item.proceso
            );

          const shouldShow =
            isVisible &&
            matchesTipo &&
            matchesEnv &&
            matchesSociedad &&
            this.corporateAuthorization.puedeAcceder(item.proceso);
          // Activar cuando el filtrado por procesos sea definitivo.
          // && matchesProcess

          if (!shouldShow) {
            return null;
          }

          const hadChildren =
            Boolean(item.children?.length);

          const children = hadChildren
            ? recursive(item.children!)
            : [];

          /*
           * Un contenedor no tiene navegación propia.
           * Solo se elimina cuando todos sus hijos
           * fueron filtrados.
           */
          const isContainer =
            !item.route &&
            !item.proceso;

          if (
            isContainer &&
            hadChildren &&
            children.length === 0
          ) {
            return null;
          }

          return {
            ...item,
            children
          };
        })
        .filter(
          (item): item is AppMenuItem =>
            item !== null
        );
    };

    return recursive(menu);
  }

  // FILTRO POR visible
  private filterVisible(menu: AppMenuItem[], tipoPV: number | undefined): AppMenuItem[] {

    const recursive = (items: AppMenuItem[]): AppMenuItem[] => {
      return items
        .map(item => {
          console.log(item);
          const children = item.children?.length && item.visible != false
            ? recursive(item.children)
            : [];

          const isVisible = item.visible !== false; // default true

          const matchesTipo =
            !item.tipoPuntoVenta?.length ||
            (tipoPV != null &&
              item.tipoPuntoVenta.includes(tipoPV));
          console.log('matchesTipo', matchesTipo, item);

          // Si no es visible y no tiene hijos visibles → eliminar
          if (!isVisible && children.length === 0 && !matchesTipo) {
            return null;
          }

          return {
            ...item,
            children
          };
        })
        .filter(Boolean) as AppMenuItem[];
    };

    return recursive(menu);
  }

  getMenuByRoute(route: string): AppMenuItem | undefined {

    const recursive = (items: AppMenuItem[]): AppMenuItem | undefined => {
      for (const item of items) {

        if (item.route === route || item.proceso === route) {
          return item;
        }

        if (item.children?.length) {
          const found = recursive(item.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    return recursive(this.rawMenu$());
  }

  searchMenu(term: string): MenuItem[] {
    const query = term?.toLowerCase().trim();
    if (!query) return [];

    const results: MenuItem[] = [];

    const searchRecursive = (items: MenuItem[], parentPath: string[] = []) => {
      for (const item of items) {
        const match =
          (item.label?.toLowerCase().includes(query)) && (item.route !== undefined);
        if (match) {
          results.push({
            ...item,
            label: `${parentPath.join(' > ')}${parentPath.length ? ' > ' : ''}${item.label}`
          });
        }

        if (item.children?.length) {
          searchRecursive(item.children, [...parentPath, item.label]);
        }
      }
    };

    searchRecursive(this.menu());

    return results;
  }

  getRawMenu(): AppMenuItem[] {
    return this.rawMenu$();
  }
}
